import { NotificationService } from '../../../shared/services/notification.service';
import {
  SelectTemplateDialogComponent,
  SelectTemplateDialogInputData,
} from '../../../shared/select-template-dialog/select-template-dialog.component';
import { Component, OnInit, ChangeDetectionStrategy, effect, inject, input, output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { filter } from 'rxjs/operators';
import { ViewTemplate } from '../../../models/template.model';
import { ReportView } from '../../../models/report.model';
import { TemplateDynamicService } from '../../../template-dynamic.service';
import { DynamicLocaleService } from '../../../dynamic-locale.service';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';


@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ppt-actions-toolbar',
  templateUrl: './actions-toolbar.component.html',
  styleUrl: './actions-toolbar.component.scss',
  imports: [NgClass, FormsModule, ReactiveFormsModule, L10nTranslationModule, MatIcon],
})
export class ActionsToolbarComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly notificationService = inject(NotificationService);
  private readonly templateDynamicService = inject(TemplateDynamicService);
  private readonly dynamicLocaleService = inject(DynamicLocaleService);
  private readonly translationService = inject(L10nTranslationService);

  readonly templates = input<ViewTemplate[]>([]);
  readonly reportData = input.required<any>();
  readonly currentView = input.required<ReportView>();
  // Default to 0 so the template + form can render before the parent has propagated the value.
  // Reading a required input synchronously in ngOnInit raced with Angular's binding step (NG0950).
  readonly recordsCount = input<number>(0);
  readonly requiresSelectTempElement = input<boolean>(true);
  readonly requiresRecordNavigation = input<boolean>(true);
  readonly isUKFeaturesEnabled = input<boolean>(false);
  readonly navRecordNum = output<number>();
  readonly templateChange = output<number>();
  readonly templateAndViewChange = output<any>();

  public selectedTemplate: ViewTemplate;
  public selectedTemplateTitle: string;
  public pageIndex: number;

  navControlsFG: FormGroup;
  errorMessage: string;
  recordNumberFC: FormControl;
  prevRecordError = false;
  nextRecordError = false;

  constructor() {
    // Build the form synchronously so [formGroup] never sees undefined.
    this.navControlsFG = this.fb.group({
      recordNumber: [
        { value: 1, disabled: false },
        [Validators.required, Validators.min(1)],
      ],
    });
    this.recordNumberFC = this.navControlsFG.get('recordNumber') as FormControl;

    effect(() => {
      const max = this.recordsCount();
      this.recordNumberFC.setValidators([Validators.required, Validators.min(1), Validators.max(max)]);
      this.recordNumberFC.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      const currentView = this.currentView();
      const templates = this.templates();
      if (currentView && templates) {
        this.selectedTemplate = templates.find(temp => temp.templateId === currentView.templateId);
        this.selectedTemplateTitle = this.selectedTemplate
          ? `${this.selectedTemplate.templateName} ${this.translationService.translate('template.templateLbl')}`
          : '';
      }
    });
  }

  ngOnInit() {
    this.navRecordNum.emit(this.recordNumberFC.value);
  }

  public onRecordNumChange() {
    const recordNumbErrors = this.recordNumberFC.errors;
    switch (true) {
      case recordNumbErrors && recordNumbErrors.hasOwnProperty('required'): {
        this.errorMessage = 'Record Number should not be empty';
        this.nextRecordError = true;
        this.prevRecordError = true;
        break;
      }
      case recordNumbErrors && recordNumbErrors.hasOwnProperty('min'): {
        this.recordNumberFC.setValue(1);
        this.prevRecordError = true;
        break;
      }
      case recordNumbErrors && recordNumbErrors.hasOwnProperty('max'): {
        this.recordNumberFC.setValue(this.recordsCount());
        this.nextRecordError = true;
        break;
      }
      default: {
        this.nextRecordError = false;
        this.prevRecordError = false;
      }
    }
    this.navRecordNum.emit(this.recordNumberFC.value);
  }

  public prevRecord() {
    this.recordNumberFC.setValue(this.recordNumberFC.value - 1);
    this.onRecordNumChange();
  }

  public nextRecord() {
    this.recordNumberFC.setValue(this.recordNumberFC.value + 1);
    this.onRecordNumChange();
  }

  public openTemplates() {
    const options = { height: '90%', width: '90%', position: { top: '30px' } };
    const data: SelectTemplateDialogInputData = {
      templates: this.templates(),
      reportId: this.reportData().reportId,
      selectedTemplateId: this.currentView().templateId,
      viewId: this.currentView().id,
    };
    this.notificationService
      .openDialog<number>(SelectTemplateDialogComponent, data, options)
      .pipe(filter((selectedTemplate: number) => selectedTemplate != null))
      .subscribe(selectedTemplateId => {
        this.fetchTemplateAndViwToSend(selectedTemplateId);
      });
  }

  private fetchTemplateAndViwToSend(selectedTemplateId: number) {
    this.templateDynamicService.setTemplateId(this.currentView().id, selectedTemplateId);
    const viewsAndTemplates = this.templateDynamicService.setViewandTemplate(
      this.dynamicLocaleService.getReportLocaleId(),
      this.currentView(),
      selectedTemplateId
    );
    if (viewsAndTemplates && viewsAndTemplates.length) {
      const templateAndViewAndSelectedId = { selectedTemplateId, viewsAndTemplates };
      viewsAndTemplates.forEach((item, index) => {
        this.templateDynamicService.saveAssociatedTemplates(this.reportData(), item).subscribe(() => {
          this.templateAndViewChange.emit(templateAndViewAndSelectedId);
        });
      });
    } else {
      this.templateChange.emit(selectedTemplateId);
    }
  }
}
